import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "../../auth/AuthProvider";
import { PlanPicker, formatPrice, planSpec } from "../../components/PlanPicker";
import { RuntimePanel } from "../../components/RuntimePanel";
import { Alert, Button, Card, CardHeader, StatusBadge, type StatusTone } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useChangeDatabasePlan, useDatabaseRuntime, useEnableDatabaseHa, usePlans } from "../../lib/queries";
import type { Database, DatabaseHa } from "../../lib/types";
import { useDatabaseTabContext } from "./context";

/*
 * Runtime and plan. The Connection card lived here too — "the thing you came
 * for" — until everything about reaching the database moved to the Access
 * tab: connection string, exposure, allowlist and password are one subject,
 * and splitting the read-side from the change-side confused more than the
 * password-adjacency argument saved.
 *
 * The plan had a card here once, moved to the page header when it was only a
 * fact to read, and is back as a card now that the tier can be changed — the
 * control belongs beside the value it changes, and a database has no settings
 * tab to put it on. Apps keep theirs on Settings; a customer moving between
 * the two finds it beside the same kind of neighbours either way.
 *
 * High availability sits under the plan, and in the same shape: one card,
 * then the confirm on that card. Developer, like the plan. No second
 * connection string and no switchover.
 */

/**
 * The badge tone for the same reading. Healthy is mint only while the writer
 * is still on the other server; a pair that has failed back is a warning.
 */
function haTone(ha: DatabaseHa | null): StatusTone {
  if (ha === null) return "neutral";
  if (ha.status === "failing_over") return "bad";
  if (ha.status === "degraded" || ha.status === "enabling") return "warn";
  return ha.protected ? "good" : "warn";
}

/**
 * Which sentence the card shows. Keys, not words: this has no translator.
 * Healthy is two sentences, depending on whether the writer is still protected.
 */
function haReading(ha: DatabaseHa | null): {
  status:
    | "overview.ha.status.off"
    | "overview.ha.status.enabling"
    | "overview.ha.status.healthy"
    | "overview.ha.status.degraded"
    | "overview.ha.status.failing_over";
  sentence:
    | "overview.ha.off"
    | "overview.ha.enabling"
    | "overview.ha.protected"
    | "overview.ha.unprotected"
    | "overview.ha.degraded"
    | "overview.ha.failingOver";
} {
  if (ha === null) return { status: "overview.ha.status.off", sentence: "overview.ha.off" };
  switch (ha.status) {
    case "enabling":
      return { status: "overview.ha.status.enabling", sentence: "overview.ha.enabling" };
    case "healthy":
      return {
        status: "overview.ha.status.healthy",
        sentence: ha.protected ? "overview.ha.protected" : "overview.ha.unprotected",
      };
    case "degraded":
      return { status: "overview.ha.status.degraded", sentence: "overview.ha.degraded" };
    case "failing_over":
      return { status: "overview.ha.status.failing_over", sentence: "overview.ha.failingOver" };
  }
}

/*
 * Omitted `ha` is an API that does not offer it, so nothing is drawn. Null is
 * off. The POST returns when the copy is accepted, not when it finishes.
 * `not_available` hides the card — the offer was withdrawn after it was shown.
 */
function HighAvailabilityCard({
  orgId,
  database,
  canManage,
}: {
  orgId: string | undefined;
  database: Database;
  canManage: boolean;
}) {
  const { t } = useTranslation("database");
  const [confirming, setConfirming] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enable = useEnableDatabaseHa(orgId ?? "");

  if (database.ha === undefined || hidden) return null;

  const ha = database.ha;
  const off = ha === null;
  const reading = haReading(ha);
  /*
   * The API's price sentence when it sent one. Off has no object, so the
   * fallback names the plan again — never a dollar amount written here.
   */
  const quotedPrice =
    ha !== null && ha.priceNote.trim() !== "" ? ha.priceNote.trim() : t("overview.ha.confirmPrice");

  async function turnOn() {
    setError(null);
    try {
      await enable.mutateAsync(database.id);
      setConfirming(false);
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "not_available") {
        setHidden(true);
        return;
      }
      setError(cause instanceof ApiError ? cause.message : t("overview.ha.failed"));
    }
  }

  return (
    <Card>
      <CardHeader
        title={t("overview.ha.title")}
        action={
          off && canManage && !confirming ? (
            <Button
              variant="secondary"
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              {t("overview.ha.enable")}
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-4 p-5">
        {error && <Alert>{error}</Alert>}
        <div className="max-w-2xl space-y-3">
          <StatusBadge tone={haTone(ha)} label={t(reading.status)} />
          <p className="text-sm leading-6 text-fg-muted">{t(reading.sentence)}</p>
        </div>
        {off && confirming && (
          <div className="max-w-2xl space-y-4 border border-line bg-canvas px-4 py-4">
            <div className="space-y-3 text-sm leading-6 text-fg-muted">
              <p>{quotedPrice}</p>
              <p>{t("overview.ha.confirmRest")}</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t("overview.cancel")}
              </Button>
              <Button loading={enable.isPending} onClick={() => void turnOn()}>
                {t("overview.ha.confirm")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function DatabaseOverviewTab() {
  const { t } = useTranslation("database");
  const { orgId, database } = useDatabaseTabContext();
  const canManage = useCan("developer");

  const runtime = useDatabaseRuntime(orgId, database.id);

  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(database.planId);
  const [planNotice, setPlanNotice] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  // Fetched only once someone opens the picker: the catalogue is dead weight
  // on the landing tab of every database that is never resized.
  const plans = usePlans(changingPlan ? orgId : undefined, "database");
  const changePlan = useChangeDatabasePlan(orgId ?? "");

  async function savePlan() {
    if (!selectedPlanId) return;
    setPlanError(null);
    setPlanNotice(null);
    try {
      const result = await changePlan.mutateAsync({
        databaseId: database.id,
        planId: selectedPlanId,
      });
      setChangingPlan(false);
      // The server's own sentence about what changed — a rate from the next
      // billed hour — rather than a hopeful one invented here.
      setPlanNotice(result.planNotice ?? t("overview.planUpdated"));
    } catch (cause) {
      setPlanError(cause instanceof ApiError ? cause.message : t("overview.planFailed"));
    }
  }

  return (
    <div className="space-y-6">
      {/* Full width, as on an app: six readings do not fit a sidebar column. */}
      <Card>
        <CardHeader title={t("runtime.title")} />
        <div className="p-5">
          {/* Against the tier's own cores, as on an app: docker's percent is
              of one core, and a database tier can allow sixteen. */}
          <RuntimePanel report={runtime.data} loading={runtime.isLoading} cpuLimit={database?.plan?.resources.cpu} />
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t("overview.plan")}
          description={t("overview.planDescription")}
          action={
            canManage && !changingPlan ? (
              <Button
                variant="secondary"
                onClick={() => {
                  // Re-read on open rather than at mount: a change made and
                  // saved leaves the mounted value pointing at the old tier.
                  setSelectedPlanId(database.planId);
                  setChangingPlan(true);
                }}
              >
                {t("overview.changePlan")}
                                </Button>
            ) : undefined
          }
        />
        <div className="space-y-4 p-5">
          {planNotice && <Alert tone="info">{planNotice}</Alert>}
          {planError && <Alert>{planError}</Alert>}

          {changingPlan ? (
            <>
              <PlanPicker
                plans={plans.data ?? []}
                loading={plans.isLoading}
                value={selectedPlanId}
                onChange={setSelectedPlanId}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setChangingPlan(false);
                    setSelectedPlanId(database.planId);
                  }}
                >
                  {t("overview.cancel")}
                                    </Button>
                <Button
                  loading={changePlan.isPending}
                  disabled={!selectedPlanId || selectedPlanId === database.planId}
                  onClick={() => void savePlan()}
                >
                  {t("overview.movePlan")}
                                    </Button>
              </div>
            </>
          ) : database.plan ? (
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-medium text-fg">{database.plan.name}</span>
              {/* Only when the name does not already state them — current tier
                  names are the specification, and repeating it beside itself
                  reads as a rendering bug. */}
              {database.plan.name !== planSpec(database.plan) && (
                <span className="text-sm text-fg-muted">{planSpec(database.plan)}</span>
              )}
              {/* "Included", not "limited to": the figure is the space the
                  tier is sold with, and nothing stops the volume growing past
                  it — the pricing page carries the full statement. */}
              {database.plan.storageGb !== null && (
                <span className="text-sm text-fg-muted">
                  {database.plan.storageGb} GB storage included
                </span>
              )}
              <span className="text-sm text-fg-subtle">
                {formatPrice(database.plan.price.cents, database.plan.price.currency)}/mo
              </span>
            </div>
          ) : (
            <p className="text-sm text-fg-subtle">
              {t("overview.noPlan")}
                            </p>
          )}
        </div>
      </Card>

      <HighAvailabilityCard orgId={orgId} database={database} canManage={canManage} />
    </div>
  );
}
